import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ValidationError } from '@lender/utils';
import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper, { ModalFormLayout } from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import { closeDialogAction } from 'store/global/actions';
import { getLoanRolesActionV2 } from 'store/system/actions';
import { selectLoanRoleUsersOptionsV2 } from 'store/system/selectors';

import { LoanRoleUserAssignmentFormTestIds } from './LoanRoleUserAssignmentForm.testIds';

const initialValues = {};

const schema = object().shape({
  user: object().required(ValidationError.RequiredField).nullable(),
});

const LoanRoleUserAssignmentForm = ({
  isLoading,
  setIsLoading,
  dialogId,
  loanRoleId,
  onSubmit,
}) => {
  const dispatch = useDispatch();

  const [isError, setIsError] = useState(false);

  const usersOptions = useSelector(selectLoanRoleUsersOptionsV2(loanRoleId));

  const {
    form,
    isFormChanged,
    validationErrors,
    setSchema,
    setInForm,
    validateField,
    validateForm,
  } = useForm(initialValues);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setIsError(false);

    dispatch(getLoanRolesActionV2())
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading]);

  useEffectSafeStrictMode(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const onSubmitForm = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const { user } = form;

    onSubmit(user.id);
    onClose();
  }, [form, onClose, onSubmit, validateForm]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      fetchData={fetchData}
      isError={isError}
      isFormChanged={isFormChanged}
      isSubmitButtonDisabled={isLoading}
      layout={ModalFormLayout.EMBEDDED}
      submitButtonName="Select"
      onClose={onClose}
      onSubmit={onSubmitForm}
    >
      <AutocompleteCDS
        required
        dataTestId={LoanRoleUserAssignmentFormTestIds.USER_SELECT}
        disabled={isLoading}
        error={!!validationErrors.user}
        helperText={validationErrors.user}
        label="User Name"
        name="user"
        options={usersOptions}
        placeholder="Select one"
        value={form.user}
        onChange={onAutocompleteChange}
      />
    </ModalFormWrapper>
  );
};

LoanRoleUserAssignmentForm.propTypes = {
  ...WithLoadingPropTypes,
  dialogId: PropTypes.string.isRequired,
  loanRoleId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default withLoading(LoanRoleUserAssignmentForm);
